/**
 * The snapshot rate (D1 `q-snapshot-rate-switch`; docs/architecture/snapshot-delivery.md §5).
 *
 * The SIMULATION always ticks at TICK_RATE (15 Hz): the input ration (AD-244), dodge timing
 * (AD-245) and every tick-counted window depend on it. Only the snapshot cadence changes. At a
 * rate below TICK_RATE a viewer is sent a snapshot on the ticks where `floor(tick × hz / 15)`
 * advances, so 10 Hz is 2 of every 3 ticks (gaps of 67 / 133 ms, 10 per second) and 15 Hz is
 * every tick (byte-identical to the build before this module).
 *
 * The rate is a per-viewer property: the server's default (`GC_SNAPSHOT_HZ`, 15 unless set)
 * applies to every client that does not ask, and a client may ask for any value in
 * SNAPSHOT_HZ_CHOICES in its `hello` / `resume` (`snapshotHz`, additive). The server echoes the
 * rate it will use in `helloAck.snapshotHz`, and the client sizes its interpolation delay from
 * it. Skipping a viewer on an off-cadence tick is exactly the backpressure skip the zone already
 * makes: its delta baseline (`sent`) stays what the client holds, so the next snapshot spans
 * both ticks and nothing is lost.
 *
 * Pure and framework-free: used by the server (cadence), the client (delay) and the harnesses.
 */
import { INTERP_DELAY_MS, TICK_MS, TICK_RATE } from './constants.js';
/** The default snapshot rate (Hz): every tick. Changing it is the owner's call (snapshot-delivery.md §5). */
export const SNAPSHOT_HZ = TICK_RATE;
/**
 * The rates a server may run and a client may request. 15 (every tick) and 10 (2 of 3 ticks)
 * are the owner's comparison (ruling 2026-10-21T12:23Z). Anything else is refused at boot
 * (server) or ignored (client request ⇒ the server default).
 */
export const SNAPSHOT_HZ_CHOICES = [15, 10];
/** True when `hz` is a rate this build runs (a member of SNAPSHOT_HZ_CHOICES). */
export function isSnapshotHz(hz) {
    return typeof hz === 'number' && SNAPSHOT_HZ_CHOICES.includes(hz);
}
/**
 * Is a snapshot due to a viewer at `hz` on server tick `tick`? Every tick at TICK_RATE; at a
 * lower rate, the ticks where `floor(tick × hz / TICK_RATE)` advances (10 Hz: 2 of 3 ticks).
 * All viewers at the same rate share the same phase, so one tick serves all of them.
 */
export function isSnapshotDue(tick, hz) {
    if (hz >= TICK_RATE)
        return true;
    return Math.floor((tick * hz) / TICK_RATE) !== Math.floor(((tick - 1) * hz) / TICK_RATE);
}
/** The longest gap between two snapshots at `hz`, in ticks (15 Hz: 1; 10 Hz: 2). */
export function snapshotMaxGapTicks(hz) {
    return Math.max(1, Math.ceil(TICK_RATE / Math.min(hz, TICK_RATE)));
}
/**
 * How far in the past a client renders remote entities at `hz`: the longest snapshot gap plus
 * one tick of jitter margin. At 15 Hz that is two ticks, INTERP_DELAY_MS (133 ms) exactly, so
 * the default path is unchanged; at 10 Hz it is three ticks, 200 ms (the longest gap is 133 ms,
 * so 133 ms of delay would leave no margin and the uneven 67/133 ms gaps would stutter).
 */
export function interpDelayMsFor(hz) {
    if (hz >= TICK_RATE)
        return INTERP_DELAY_MS;
    return Math.round((snapshotMaxGapTicks(hz) + 1) * TICK_MS);
}
//# sourceMappingURL=snapshot-rate.js.map